import React, { useMemo } from 'react';
import { addDoc, collection } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { normalizeType, normalizeKey } from '../utils/normalize';

export default function CreateIrishSetForm({ user, inputStyle, tunes, showForm }) {
  const [irishType, setIrishType] = React.useState('');
  const [keys, setKeys] = React.useState(['', '', '']);
  const navigate = useNavigate();
  const [keyMode, setKeyMode] = React.useState('manual'); // 'manual' or 'surprise'

  const allIrishTunes = useMemo(() => {
    return (tunes || []).filter(t => t.genre === 'Irish');
  }, [tunes]);

  const allTypes = useMemo(() => {
    const typeSet = new Set();
    allIrishTunes.forEach(t => {
      const norm = normalizeType(t.type);
      if (norm) typeSet.add(norm);
    });
    return Array.from(typeSet).sort();
  }, [allIrishTunes]);

  const allMatchingTunes = useMemo(() => {
    return allIrishTunes.filter(t =>
      normalizeType(t.type) === normalizeType(irishType)
    );
  }, [allIrishTunes, irishType]);

  const keyCounts = useMemo(() => {
    const counts = {};
    allMatchingTunes.forEach(t => {
      const norm = normalizeKey(t.key);
      if (norm) counts[norm] = (counts[norm] || 0) + 1;
    });
    return counts;
  }, [allMatchingTunes]);

  const availableKeysWithCounts = useMemo(() => {
    const usedCounts = keys.reduce((acc, key) => {
      const norm = normalizeKey(key);
      if (norm) acc[norm] = (acc[norm] || 0) + 1;
      return acc;
    }, {});

    const result = {};
    Object.entries(keyCounts).forEach(([key, total]) => {
      const used = usedCounts[key] || 0;
      result[key] = total - used;
    });
    return result;
  }, [keys, keyCounts]);

  const handleKeyChange = (index, value) => {
    const newKeys = [...keys];
    newKeys[index] = value;
    setKeys(newKeys);
  };

  const handleRemoveKey = (index) => {
    if (keys.length <= 2) return;
    const newKeys = keys.filter((_, i) => i !== index);
    setKeys(newKeys);
  };

const handleCreateSet = async () => {
  const selected = [];
  const usedTuneIds = new Set();

  let matchingPool = allMatchingTunes;

  if (keyMode === 'manual') {
    const validKeys = keys.map(normalizeKey).filter(Boolean);
    for (const key of validKeys) {
      const matches = matchingPool.filter(t =>
        normalizeKey(t.key) === key && !usedTuneIds.has(t.id)
      );
      if (matches.length > 0) {
        const randomIndex = Math.floor(Math.random() * matches.length);
        const chosen = matches[randomIndex];
        selected.push(chosen);
        usedTuneIds.add(chosen.id);
      }
    }
  } else {
    // Surprise mode: pick N random tunes from full pool of correct type, no repeats
    const available = matchingPool.filter(t => !usedTuneIds.has(t.id));
    const shuffled = [...available].sort(() => 0.5 - Math.random());

    for (let i = 0; i < keys.length && i < shuffled.length; i++) {
      selected.push(shuffled[i]);
      usedTuneIds.add(shuffled[i].id);
    }
  }

  if (selected.length < keys.length) {
    alert("Not enough unique tunes found to create a complete set.");
    return;
  }

const titlePrefix =
  keyMode === 'surprise'
    ? `${irishType} Set (Surprise)`
    : `${irishType} Set (Generated)`;
const title = `${titlePrefix}: ${selected.map(t => normalizeKey(t.key)).join(' / ')}`;
  const newSet = {
    title,
    notes: '',
    tunes: selected.map(t => ({ id: t.id, title: t.title })),
    createdAt: new Date().toISOString()
  };

  const ref = await addDoc(collection(db, 'users', user.uid, 'sets'), newSet);
  navigate(`/sets/${ref.id}`);
};



  const addKeyField = () => {
    if (keys.length >= 5) return;
    setKeys([...keys, '']);
  };

  if (!showForm) return null;

  return (
    <div style={{ marginTop: '0.75rem', textAlign: 'center' }}>
      <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <select value={irishType} onChange={e => {
          setIrishType(e.target.value);
          setKeys(['', '', '']);
        }}>
          <option value="">Type</option>
          {allTypes.map((t, i) => (
            <option key={i} value={t}>{t}</option>
          ))}
        </select>

<select value={keyMode} onChange={e => setKeyMode(e.target.value)}>
  <option value="manual">Select keys</option>
  <option value="surprise">Surprise me</option>
</select>

        <button
          style={{ backgroundColor: '#228be6', color: 'white', height: 'fit-content', padding: '6px 12px' }}
          onClick={handleCreateSet}
        >
          ➕ Create
        </button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}>
        {keys.map((val, idx) => (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
<select
  value={val}
  onChange={e => handleKeyChange(idx, e.target.value)}
  disabled={keyMode === 'surprise'}
>
              <option value="">Key {idx + 1}</option>
              {Object.entries(keyCounts)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([k]) => {
                  const remaining = availableKeysWithCounts[k] ?? 0;
                  return (
                    <option key={k} value={k} disabled={remaining <= 0}>
                      {remaining <= 0 ? k : `${k} (${remaining})`}
                    </option>
                  );
                })}
            </select>
            {keys.length > 2 && (
              <button
                type="button"
                onClick={() => handleRemoveKey(idx)}
                style={{ padding: '0.1rem 0.3rem', fontSize: '0.75rem' }}
              >
                ✖
              </button>
            )}
          </div>
        ))}

        {keys.length < 5 && (
          <button
            type="button"
            style={{ backgroundColor: '#228be6', color: 'white', height: 'fit-content', padding: '6px 10px' }}
            onClick={addKeyField}
          >
            ➕
          </button>
        )}
      </div>
    </div>
  );
}
